import classNames from 'classnames'
import React from 'react'
import { Spinner } from '@blueprintjs/core'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  IUI2FormSectionErrorContext,
  UI2FormSectionErrorContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/error-context'
import {
  IUI2FormSectionFillContext,
  UI2FormSectionFillContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/fill-context'
import { UI2FormSectionIdContext } from 'shared-libs/components/ui2/forms/ui2-form-section/section-id-context'
import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { UI2FormSectionSubmitContext } from 'shared-libs/components/ui2/forms/ui2-form-section/submit-context'
import { UI2FormSectionConfigProps as UI2FormSectionProps } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section.config'
import {
  UI2FormContextProps as IUI2FormContext,
  UI2FormContext,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-form.context'
import { FormDensity } from 'shared-libs/components/ui2/forms/ui2-form/ui2-form.config'
import { UI2WizardContext } from 'shared-libs/components/ui2/forms/ui2-form/wizard-context'
import { UI2FormSectionStatus } from 'shared-libs/components/ui2/forms/ui2-form/wizard-controller'

import { REVEAL_EVENT } from 'browser/components/atomic-elements/atoms/section/section'

import { UI2Label } from '../../fields/field-ui/label/label'
import { resolveUI2SpacingStyle } from '../../ui2-spacing'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/icon-names'
import './_ui2-form-section.scss'

/**
 * @uiComponent
 */
interface IUI2FormSectionProps extends UI2FormSectionProps {
  formDensity?: FormDensity
  frames?: FramesManager
  translationTable?: any
  children?: React.ReactNode
}

interface IUI2FormSectionState {
  isCollapsed: boolean
  hasReceivedError: boolean
  errorCount: number
  showRequiredErrors: boolean
}

function shallowArrayEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * A group of UI2 fields within a `ui2:form`. Owns collapse state, aggregates
 * descendant error/fill reporting up to the form, and — in wizard/stacked mode
 * — registers as a step, renders its Back/Skip/Next footer, and drives
 * advancement and required-error surfacing.
 */
export class UI2FormSection extends React.Component<IUI2FormSectionProps, IUI2FormSectionState> {
  public static contextType = UI2WizardContext
  public declare context: React.ContextType<typeof UI2WizardContext>

  private bodyRef = React.createRef<HTMLDivElement>()
  private erroredFieldIds = new Set<string>()
  private unfilledFieldIds = new Set<string>()
  private sectionId = `ui2FormSection:${Math.random().toString(36).slice(2)}`
  private lastEmittedStatus: UI2FormSectionStatus | undefined
  private parentErrorContext: IUI2FormSectionErrorContext | null = null
  private parentFillContext: IUI2FormSectionFillContext | null = null
  private focusTargetContext: IFormFocusTargetContext | null = null
  private wasActiveLastRender = false

  // Manual useMemo for the child form context (class component): cache the value
  // and its primitive dependency tuple, rebuild only when a dep changes.
  private childFormContextDeps?: unknown[]
  private childFormContextValue?: IUI2FormContext

  private resolveChildFormContext(
    deps: unknown[],
    build: () => IUI2FormContext,
  ): IUI2FormContext {
    if (this.childFormContextDeps && shallowArrayEqual(this.childFormContextDeps, deps)) {
      return this.childFormContextValue!
    }
    this.childFormContextDeps = deps
    this.childFormContextValue = build()
    return this.childFormContextValue
  }

  private childContext: IUI2FormSectionErrorContext = {
    reportError: (id, hasError) => {
      if (id === this.sectionId) return
      const wasEmpty = this.erroredFieldIds.size === 0
      if (hasError) this.erroredFieldIds.add(id)
      else this.erroredFieldIds.delete(id)
      const isEmpty = this.erroredFieldIds.size === 0
      if (!isEmpty && this.state.isCollapsed) {
        this.setState({ isCollapsed: false })
      }
      const nextHasReceived = this.state.hasReceivedError || !isEmpty
      if (
        this.state.errorCount !== this.erroredFieldIds.size ||
        nextHasReceived !== this.state.hasReceivedError
      ) {
        this.setState({ errorCount: this.erroredFieldIds.size, hasReceivedError: nextHasReceived })
      }
      if (wasEmpty !== isEmpty) {
        this.parentErrorContext?.reportError(this.sectionId, !isEmpty)
      }
      if (
        this.context?.isWizard &&
        this.context.activeSectionId === this.sectionId &&
        this.getEffectiveNextMode() === 'automatic' &&
        isEmpty &&
        nextHasReceived
      ) {
        this.context.advance(this.sectionId)
      }
    },
  }

  private childFillContext: IUI2FormSectionFillContext = {
    reportUnfilled: (id, isUnfilled) => {
      if (id === this.sectionId) return
      const wasEmpty = this.unfilledFieldIds.size === 0
      if (isUnfilled) this.unfilledFieldIds.add(id)
      else this.unfilledFieldIds.delete(id)
      const isEmpty = this.unfilledFieldIds.size === 0
      if (wasEmpty !== isEmpty) {
        this.parentFillContext?.reportUnfilled(this.sectionId, !isEmpty)
      }
    },
  }

  constructor(props: IUI2FormSectionProps) {
    super(props)
    this.state = {
      isCollapsed: props.isCollapsed ?? false,
      hasReceivedError: false,
      errorCount: 0,
      showRequiredErrors: false,
    }
  }

  public componentDidUpdate(prevProps: IUI2FormSectionProps) {
    if (prevProps.isCollapsed !== this.props.isCollapsed) {
      this.setState({ isCollapsed: this.props.isCollapsed ?? false })
    }
    this.emitStatus()
    const wizardCtx = this.context
    if (!wizardCtx?.isWizard) {
      this.wasActiveLastRender = false
      return
    }
    const isNowActive = wizardCtx.activeSectionId === this.sectionId
    const justActivated = isNowActive && !this.wasActiveLastRender
    this.wasActiveLastRender = isNowActive
    if (
      justActivated &&
      !wizardCtx.isReplaying &&
      this.getEffectiveNextMode() === 'automatic' &&
      this.erroredFieldIds.size === 0
    ) {
      wizardCtx.advance(this.sectionId)
    }
  }

  public componentDidMount() {
    this.bodyRef.current?.addEventListener(REVEAL_EVENT, this.handleReveal)
    if (this.context?.isWizard) {
      this.context.register({
        id: this.sectionId,
        title: this.props.title,
        entryPredicate: this.props.entryPredicate,
        nextMode: this.props.nextMode ?? 'submit',
        allowSkip: !!this.props.allowSkip,
      })
    }
    this.emitStatus()
  }

  public componentWillUnmount() {
    this.bodyRef.current?.removeEventListener(REVEAL_EVENT, this.handleReveal)
    if (this.erroredFieldIds.size > 0) {
      this.parentErrorContext?.reportError(this.sectionId, false)
    }
    if (this.unfilledFieldIds.size > 0) {
      this.parentFillContext?.reportUnfilled(this.sectionId, false)
    }
    if (this.context?.isWizard) {
      this.context.deregister(this.sectionId)
    }
  }

  public render() {
    return (
      <FormFocusTargetContext.Consumer>
        {(focusCtx) => {
          this.focusTargetContext = focusCtx
          return (
            <UI2FormSectionErrorContext.Consumer>
              {(parentErrorCtx) => {
                this.parentErrorContext = parentErrorCtx
                return (
                  <UI2FormSectionFillContext.Consumer>
                    {(parentFillCtx) => {
                      this.parentFillContext = parentFillCtx
                      return this.renderInner()
                    }}
                  </UI2FormSectionFillContext.Consumer>
                )
              }}
            </UI2FormSectionErrorContext.Consumer>
          )
        }}
      </FormFocusTargetContext.Consumer>
    )
  }

  private renderInner() {
    const {
      formDensity = 'default',
      isReadOnly,
      isDisabled,
      isRequired,
      isOptional,
      enablePrefilledMode,
      isHorizontalLayout,
      title,
      isCollapsable,
      children,
    } = this.props
    const { isCollapsed } = this.state
    const wizardCtx = this.context
    const isWizard = !!wizardCtx?.isWizard
    const isHiddenByWizard =
      isWizard && wizardCtx.mode === 'wizard'
        ? wizardCtx.activeSectionId !== this.sectionId
        : wizardCtx?.mode === 'stacked' &&
          !this.getIsWizardStepComplete() &&
          wizardCtx.activeSectionId !== this.sectionId
    const isActiveByWizard = isWizard && wizardCtx.activeSectionId === this.sectionId
    const renderIsCompleteByWizard =
      isWizard && wizardCtx.mode === 'stacked' && this.getIsWizardStepComplete()
    const isReplaying = isWizard && !!wizardCtx?.isReplaying
    const sectionStatus = wizardCtx?.sectionStatuses[this.sectionId]
    // Suppress required-field validation when a section is definitively not applicable:
    // either the wizard already evaluated and rejected its predicate (notApplicable), or
    // the active step is the final one — meaning every remaining queued section would be
    // skipped on advance, so none of them can become applicable from intermediate steps.
    const isNotApplicableSection =
      sectionStatus === 'notApplicable' ||
      (!!wizardCtx?.isFinalStep && sectionStatus !== 'complete' && sectionStatus !== 'active')
    const childFormContext = this.resolveChildFormContext(
      [
        formDensity,
        renderIsCompleteByWizard,
        isReadOnly,
        isDisabled,
        isRequired,
        isOptional,
        enablePrefilledMode,
        isHorizontalLayout,
        isNotApplicableSection,
      ],
      () => ({
        formDensity: renderIsCompleteByWizard ? 'compact' : formDensity,
        isReadOnly: isReadOnly || renderIsCompleteByWizard,
        isDisabled,
        isRequired,
        isOptional,
        enablePrefilledMode,
        isHorizontalLayout,
        ...(renderIsCompleteByWizard ? { inlineHelpText: null } : undefined),
        ...(isNotApplicableSection ? { sectionIsNotApplicable: true } : undefined),
      }),
    )
    return (
      <UI2FormContext.Provider value={childFormContext}>
        <UI2FormSectionIdContext.Provider value={this.sectionId}>
          <UI2FormSectionErrorContext.Provider value={this.childContext}>
            <UI2FormSectionFillContext.Provider value={this.childFillContext}>
              <UI2FormSectionSubmitContext.Provider value={this.state.showRequiredErrors}>
                <div
                  className={classNames('ui2-formSection', `ui2-formSection--${formDensity}`, {
                    'ui2-formSection--complete': renderIsCompleteByWizard,
                    'is-active--wizard': isActiveByWizard && wizardCtx?.mode === 'wizard',
                    'is-active--stacked': isActiveByWizard && wizardCtx?.mode === 'stacked',
                  })}
                  style={
                    isHiddenByWizard
                      ? { display: 'none' }
                      : resolveUI2SpacingStyle(this.props)
                  }
                >
                  {renderIsCompleteByWizard ? (
                    this.renderCompleteHeader()
                  ) : (title || isCollapsable) ? (
                    <div className={classNames('ui2-formSectionHeader')}>
                      {this.renderHeaderContent()}
                    </div>
                  ) : null}
                  <div
                    ref={this.bodyRef}
                    className={classNames(
                      'ui2-formSectionBody',
                      `ui2-formSectionBody--${formDensity}`,
                      {
                        'is-collapsed': isCollapsed,
                        'ui2-form-sectionBody--replaying': isReplaying && !isHiddenByWizard,
                      }
                    )}
                  >
                    {children}
                  </div>
                  {isReplaying && !isHiddenByWizard && (
                    <div className="ui2-form-sectionReplay">
                      <Spinner size={20} />
                    </div>
                  )}
                  {isWizard && isActiveByWizard && !isReplaying && this.renderWizardFooter()}
                </div>
              </UI2FormSectionSubmitContext.Provider>
            </UI2FormSectionFillContext.Provider>
          </UI2FormSectionErrorContext.Provider>
        </UI2FormSectionIdContext.Provider>
      </UI2FormContext.Provider>
    )
  }

  private renderWizardFooter() {
    const { allowSkip } = this.props
    const nextMode = this.getEffectiveNextMode()
    const wizardCtx = this.context!
    const hasErrors = this.state.errorCount > 0
    const isActiveFinalStep = wizardCtx.isFinalStep && wizardCtx.activeSectionId === this.sectionId
    const canSkip = allowSkip && !isActiveFinalStep && wizardCtx.mode === 'wizard'
    return (
      <div className="ui2-form-sectionFooter">
        {wizardCtx.canGoBack && (
          <button type="button" className="ui2-form-sectionFooter__back" onClick={wizardCtx.back}>
            Back
          </button>
        )}
        {canSkip && (
          <button
            type="button"
            className="ui2-form-sectionFooter__skip"
            onClick={() => wizardCtx.skip(this.sectionId)}
          >
            Skip
          </button>
        )}
        {nextMode === 'submit' && (
          <button
            type="button"
            className={classNames('ui2-form-sectionFooter__next', {
              'ui2-form-sectionFooter__next--muted': hasErrors,
            })}
            onClick={this.handleNext}
          >
            {wizardCtx.isFinalStep && wizardCtx.activeSectionId === this.sectionId
              ? 'Complete'
              : 'Next'}
          </button>
        )}
      </div>
    )
  }

  private renderCompleteHeader() {
    const { title, translationTable, frames } = this.props
    const resolvedTranslationTable = Object.assign(
      {},
      translationTable,
      frames?.getContext('translationTable')
    )
    // role=button (not <button>) so UI2Label's truncation Popover / tooltip
    // markup isn't nested inside a native button.
    return (
      <div
        className="ui2-formSectionHeader ui2-formSectionHeader--editable"
        role="button"
        tabIndex={0}
        onClick={this.handleEditCompleted}
        onKeyDown={this.handleEditKeyDown}
        aria-label={title ? `Edit ${title}` : 'Edit section'}
      >
        {title && (
          <UI2Label
            className="ui2-formSectionHeader-label"
            labelText={title}
            translationTable={resolvedTranslationTable}
          />
        )}
        <span className="ui2-formSectionHeader-editAffordance">
          <UI2Icon name={UI2IconNames.PENCIL} size={14} />
          Edit
        </span>
      </div>
    )
  }

  private renderHeaderContent() {
    const { title, tooltip, inlineHelpText, isCollapsable, translationTable, frames } = this.props
    const { isCollapsed } = this.state
    const resolvedTranslationTable = Object.assign(
      {},
      translationTable,
      frames?.getContext('translationTable')
    )
    const hasTitle = !!title
    if (!hasTitle && !isCollapsable) return null
    return (
      <>
        {hasTitle && (
          <UI2Label
            className="ui2-formSectionHeader-label"
            labelText={title}
            tooltipText={typeof tooltip === 'string' ? tooltip : tooltip?.join('\n')}
            inlineHelpText={
              typeof inlineHelpText === 'string' ? inlineHelpText : inlineHelpText?.join('\n')
            }
            translationTable={resolvedTranslationTable}
          />
        )}
        {isCollapsable && (
          <button
            className="ui2-formSectionHeader-collapseButton"
            onClick={this.toggleCollapsed}
            type="button"
            aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
          >
            <UI2Icon name={isCollapsed ? UI2IconNames.CHEVRON_DOWN : UI2IconNames.CHEVRON_UP} size={14} />
          </button>
        )}
      </>
    )
  }

  // Push the section's derived status out through the bound `onChange`. In
  // wizard mode the status comes from the form's projection of the wizard
  // queue and is unavailable until the section has registered — nothing is
  // emitted on that first render. In default mode there is no queue, so the
  // section mirrors how `ui2:form` derives its own default-mode status: clean
  // is 'complete', anything else is 'upcoming'.
  private emitStatus() {
    const { onChange, value } = this.props
    if (!onChange) return
    const wizardCtx = this.context
    const status: UI2FormSectionStatus | undefined = wizardCtx?.isWizard
      ? wizardCtx.sectionStatuses[this.sectionId]
      : this.state.errorCount === 0
        ? 'complete'
        : 'upcoming'
    if (status == null) return
    if (this.lastEmittedStatus === status) return
    if (value === status) {
      this.lastEmittedStatus = status
      return
    }
    this.lastEmittedStatus = status
    // Silent: derived state, must not dirty the workflow.
    onChange(status, true)
  }

  // A previously-completed automatic-mode section is treated as submit-mode on
  // revisit — the user must manually press Next to reconfirm any edits, and no
  // auto-advance fires.
  private getEffectiveNextMode(): 'submit' | 'automatic' {
    const declared = this.props.nextMode ?? 'submit'
    if (declared !== 'automatic') return 'submit'
    const wasEverCompleted = !!this.context?.progressSteps.find((s) => s.id === this.sectionId)
      ?.wasEverCompleted
    return wasEverCompleted ? 'submit' : 'automatic'
  }

  private getIsWizardStepComplete(): boolean {
    const wizardCtx = this.context
    if (!wizardCtx?.isWizard) return false
    const step = wizardCtx.progressSteps.find((s) => s.id === this.sectionId)
    return !!step?.isCompleted
  }

  private handleNext = () => {
    if (this.erroredFieldIds.size > 0) {
      // Surface field-level red glow on the first blocked Next click. Every
      // subsequent click keeps the flag on so newly-emptied fields also glow.
      this.setState({ hasReceivedError: true, showRequiredErrors: true }, () => {
        this.focusTargetContext?.focusFirstError({ sectionId: this.sectionId })
      })
      return
    }
    this.context?.advance(this.sectionId)
  }

  private toggleCollapsed = () => {
    this.setState((prev) => ({ isCollapsed: !prev.isCollapsed }))
  }

  private handleEditCompleted = () => {
    this.context?.jumpTo(this.sectionId)
  }

  private handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.handleEditCompleted()
    }
  }

  private handleReveal = () => {
    if (this.state.isCollapsed) {
      this.setState({ isCollapsed: false })
    }
  }
}
